import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  makeStyles,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
} from '@material-ui/icons';
import { CSVLink } from 'react-csv';
import ConversionsTable from '../../components/affiliate/ConversionsTable';
// ConversionsTable handles its own queries

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#02172a',
    minHeight: '100vh',
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  mainHeaderTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  mainHeaderSubtitle: {
    color: '#b0b0b0',
    fontSize: '1.1rem',
    lineHeight: 1.5,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
    },
  },
  headerCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    marginBottom: theme.spacing(3),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 20px rgba(25, 118, 210, 0.1)',
      borderColor: '#1976d2',
    },
  },
  headerContent: {
    padding: theme.spacing(3),
  },
  headerTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1.5rem',
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    color: '#b0b0b0',
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    backgroundColor: 'transparent',
    color: '#1976d2',
    border: '1px solid #1976d2',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
      borderColor: '#1565c0',
    },
  },
  exportButton: {
    backgroundColor: '#1976d2',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  },
  conversionsCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    marginBottom: theme.spacing(3),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 20px rgba(25, 118, 210, 0.1)',
      borderColor: '#1976d2',
    },
  },
  conversionsContent: {
    padding: theme.spacing(3),
  },
  detailDialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1a2332',
      border: '1px solid #2a3a4a',
      borderRadius: 12,
    },
  },
  detailTitle: {
    color: '#fff',
    borderBottom: '1px solid #2a3a4a',
    paddingBottom: theme.spacing(2),
    fontWeight: 600,
  },
  detailContent: {
    color: '#b0b0b0',
    padding: theme.spacing(3),
  },
  detailGrid: {
    marginBottom: theme.spacing(2),
  },
  detailLabel: {
    color: '#1976d2',
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  detailValue: {
    color: '#fff',
    marginBottom: theme.spacing(1),
  },
}));

interface Conversion {
  id: string;
  affiliateId: string;
  referralCode: string;
  artistId: string;
  artist: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  commissionAmount: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  approvedAt?: string;
  paidAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

const AdminConversions: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedConversion, setSelectedConversion] = useState<Conversion | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Note: ConversionsTable component handles its own query
  // This allows for proper pagination and filtering within the table

  // Get conversions data for CSV export - this will be populated by ConversionsTable
  // For now, use empty array - CSV export can be enhanced later to fetch all data
  const conversions: Conversion[] = [];
  const csvData = conversions.map((conversion: Conversion) => ({
    'Referral Code': conversion?.referralCode || 'N/A',
    'Artist Name': conversion.artist?.firstName && conversion.artist?.lastName 
      ? `${conversion.artist.firstName} ${conversion.artist.lastName}`
      : conversion.artist?.email || 'N/A',
    'Artist Email': conversion.artist?.email || 'N/A',
    'Conversion Date': conversion.createdAt ? new Date(conversion.createdAt).toLocaleDateString() : 'N/A',
    'Commission Amount': conversion.commissionAmount ? `$${conversion.commissionAmount.toFixed(2)}` : '$0.00',
    'Status': conversion.status ? conversion.status.toUpperCase() : 'UNKNOWN',
    'Total Revenue': conversion.commissionAmount ? `$${conversion.commissionAmount.toFixed(2)}` : '$0.00',
  }));

  const handleViewConversion = (conversion: Conversion) => {
    setSelectedConversion(conversion);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedConversion(null);
  };

  // ConversionsTable handles its own loading/error states

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.mainHeaderTitle}>
          Affiliate Conversions
        </Typography>
        <Typography variant="body1" className={classes.mainHeaderSubtitle}>
          View and manage all affiliate conversions across the platform
        </Typography>
        <Box className={classes.actionButtons}>
          <CSVLink
            data={csvData}
            filename={`admin-conversions-${new Date().toISOString().split('T')[0]}.csv`}
            style={{ textDecoration: 'none' }}
          >
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              className={classes.exportButton}
            >
              Export CSV
            </Button>
          </CSVLink>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            className={classes.actionButton}
          >
            Advanced Filters
          </Button>
        </Box>
      </Box>

      {/* Conversions Overview Card */}
      <Card className={classes.headerCard}>
        <CardContent className={classes.headerContent}>
          <Typography variant="h5" className={classes.headerTitle}>
            Conversion Overview
          </Typography>
          <Typography variant="body1" className={classes.headerSubtitle}>
            View and manage all affiliate conversions across the platform
          </Typography>
        </CardContent>
      </Card>

      {/* Conversions Table */}
      <Card className={classes.conversionsCard}>
        <CardContent className={classes.conversionsContent}>
          <ConversionsTable onViewConversion={handleViewConversion} />
        </CardContent>
      </Card>

      {/* Conversion Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
        className={classes.detailDialog}
      >
        <DialogTitle className={classes.detailTitle}>
          Conversion Details
        </DialogTitle>
        <DialogContent className={classes.detailContent}>
          {selectedConversion && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box className={classes.detailGrid}>
                  <Typography variant="subtitle2" className={classes.detailLabel}>
                    Referral Code
                  </Typography>
                  <Typography variant="body1" className={classes.detailValue}>
                    {selectedConversion.referralCode || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.detailGrid}>
                  <Typography variant="subtitle2" className={classes.detailLabel}>
                    Artist Name
                  </Typography>
                  <Typography variant="body1" className={classes.detailValue}>
                    {selectedConversion.artist?.firstName && selectedConversion.artist?.lastName 
                      ? `${selectedConversion.artist.firstName} ${selectedConversion.artist.lastName}`
                      : selectedConversion.artist?.email || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.detailGrid}>
                  <Typography variant="subtitle2" className={classes.detailLabel}>
                    Artist Email
                  </Typography>
                  <Typography variant="body1" className={classes.detailValue}>
                    {selectedConversion.artist?.email || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.detailGrid}>
                  <Typography variant="subtitle2" className={classes.detailLabel}>
                    Conversion Date
                  </Typography>
                  <Typography variant="body1" className={classes.detailValue}>
                    {selectedConversion.createdAt ? new Date(selectedConversion.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.detailGrid}>
                  <Typography variant="subtitle2" className={classes.detailLabel}>
                    Commission Amount
                  </Typography>
                  <Typography variant="body1" className={classes.detailValue}>
                    ${selectedConversion.commissionAmount ? selectedConversion.commissionAmount.toFixed(2) : '0.00'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.detailGrid}>
                  <Typography variant="subtitle2" className={classes.detailLabel}>
                    Status
                  </Typography>
                  <Chip
                    label={selectedConversion.status ? selectedConversion.status.toUpperCase() : 'UNKNOWN'}
                    color={selectedConversion.status === 'paid' ? 'primary' : 'secondary'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box className={classes.detailGrid}>
                  <Typography variant="subtitle2" className={classes.detailLabel}>
                    Total Revenue
                  </Typography>
                  <Typography variant="h6" className={classes.detailValue}>
                    ${selectedConversion.commissionAmount ? selectedConversion.commissionAmount.toFixed(2) : '0.00'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog} className={classes.actionButton}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminConversions;
